import React from 'react';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  CircularProgress,
  Checkbox,
  Typography,
  TextField,
  TableContainer,
  Box,
  useTheme,
  useMediaQuery,
  AppBar,
  Toolbar,
  IconButton
} from '@material-ui/core';
import { MedicalSupplyOrderViewModel, MedicalSupplyOrderItemViewModel } from '../../models/injection';
import { useStyles } from './styles';
import { Title } from '../../elements';
import { MedicalSuppliesService } from '../../hooks';
import { AnimalType } from '../../models/animal';
import { renderSupplyRowLocations } from '../../components/OrderMedicalSupplyModal/component';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@material-ui/icons/Close';

interface CompleteMedicalSupplyOrderModalProps {
  opened: boolean;
  order: MedicalSupplyOrderViewModel;
  onModalAction: (result: boolean | null) => void;
}

const CompleteMedicalSupplyOrderModal: React.FC<any> = ({
  opened,
  order,
  onModalAction
}: CompleteMedicalSupplyOrderModalProps) => {
  const medicalSuppliesService = new MedicalSuppliesService();
  const classes = useStyles();
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [isBusy, setIsBusy] = React.useState(false);
  const [orderItems, setOrderItems] = React.useState([] as MedicalSupplyOrderItemViewModel[]);

  React.useEffect(() => {
    if (order && order.items) {
      setOrderItems(order.items);
    }
  }, [opened]);

  const handleOrderItemDeliveredAmountChange = (value: number, index: number) => {
    orderItems[index].realAmount = value;
    setOrderItems([...orderItems]);
  };

  const closeDialog = (result: boolean) => {
    if (onModalAction) {
      onModalAction(result);
    }

    clearFields();
  };

  const handleCompleteOrder = () => {
    if (!validForm()) {
      return;
    }

    setIsBusy(true);

    const completedOrder = {
      ...order,
      items: orderItems
    };

    medicalSuppliesService
      .completeMedicalSupplyOrder(completedOrder)
      .then(() => {
        notifications.success(t('f-supp-order-completed-ok'));
        closeDialog(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const validForm = (): boolean => {
    const delivered = orderItems.filter((x) => x.delivered);

    if (delivered.filter((x) => x.realAmount <= 0).length) {
      notifications.warning(t('f-val-supp-order-real'));
      return false;
    }

    return true;
  };

  const clearFields = () => {
    setOrderItems([]);
  };

  const renderOrderItemDeliveredAmountCell = (item: MedicalSupplyOrderItemViewModel, index: number) => {
    return (
      <TextField
        id={`delivered-${index}`}
        type="number"
        size="small"
        variant="outlined"
        label={undefined}
        disabled={!item.delivered}
        className={classes.amountInput}
        value={item.realAmount}
        onChange={(e) => {
          const newValue = Number(e.target.value);
          if (newValue >= 0) {
            handleOrderItemDeliveredAmountChange(newValue, index);
          }
        }}
      />
    );
  };

  const renderOrderItemsSectionHeader = () => {
    return (
      <Grid item container direction="row" md={12}>
        <Grid item container md={8} spacing={1}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'shopping_bag'}
            </Icon>
          </Grid>
          <Grid item>
            <Title>{t('f-supp-order-items')}</Title>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionHeader = () => {
    return (
      <Grid item container direction="row" md={12} className={classes.mt2}>
        <Grid item container spacing={1}>
          <Grid item>
            <Icon color="primary" fontSize="large">
              {'comment'}
            </Icon>
          </Grid>
          <Grid item>
            <Title>{t('f-supp-order-details')}</Title>
          </Grid>
        </Grid>
      </Grid>
    );
  };

  const renderDetailsSectionContent = () => {
    const totalAmount = orderItems.reduce((a, b) => a + b.amount, 0);

    return (
      <Grid item container direction="row" spacing={2}>
        {order.confirmation && order.confirmation.dateTime && (
          <Grid item md={3}>
            <Typography variant="overline">{t('f-supp-order-confirm-date')}</Typography>
            <Typography variant="body1">{new Date(order.confirmation.dateTime).toLocaleString()}</Typography>
          </Grid>
        )}

        <Grid item md={3}>
          <Typography variant="overline">{t('f-supp-order-total-amount')}</Typography>
          <Typography variant="body1">{totalAmount}</Typography>
        </Grid>

        <Grid item md={3}>
          <Typography variant="overline">{t('f-supp-order-comments')}</Typography>
          <Typography variant="body1">{order.details?.comments || '-'}</Typography>
        </Grid>
      </Grid>
    );
  };

  const renderOrderItemsTable = () => {
    return (
      <Grid item xs={12} className={classes.mt2}>
        <TableContainer component={Paper} variant="outlined" style={{ height: '55vh' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('f-com-supply')}</TableCell>
                <TableCell>{t('f-com-animal-type')}</TableCell>
                <TableCell align="center">{t('f-supp-order-locations')}</TableCell>
                <TableCell>{t('f-supp-order-amount')}</TableCell>
                <TableCell>{t('f-supp-order-amount-del')}</TableCell>
                <TableCell padding="checkbox">{t('f-supp-order-delivered')}</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {order &&
                order.items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{`${item.medicalSupplyBottle.medicalSupply.number} ${item.medicalSupplyBottle.size}`}</TableCell>
                    <TableCell>{AnimalType[item.medicalSupplyBottle.animalType].toString()}</TableCell>
                    <TableCell align="center">{renderSupplyRowLocations(item, index)}</TableCell>
                    <TableCell>{item.amount}</TableCell>
                    <TableCell>{renderOrderItemDeliveredAmountCell(item, index)}</TableCell>
                    <TableCell>{renderSupplyRowActionCell(item, index)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    );
  };

  const renderSupplyRowActionCell = (item: MedicalSupplyOrderItemViewModel, index: number) => {
    return (
      <Checkbox checked={item.delivered || false} color="primary" onChange={(e) => handleDeliveredChange(e, index)} />
    );
  };

  const handleDeliveredChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    orderItems[index].delivered = event.target.checked;
    setOrderItems([...orderItems]);
  };

  if (!order || !order.items) {
    return null;
  }

  return (
    <Dialog
      PaperProps={{
        style: {
          minHeight: 'calc(100vh - 64px)'
        }
      }}
      id="completeOrderDialog"
      open={opened}
      onClose={() => closeDialog(false)}
      className={classes.dialog}
      maxWidth="lg"
      fullScreen={fullScreen}
      fullWidth
      aria-labelledby="form-dialog-title"
    >
      {fullScreen && (
        <AppBar>
          <Toolbar>
            <Box width="100%" justifyContent="space-between" display="flex">
              <Box display="flex" alignItems="center">
                <IconButton edge="start" color="inherit" onClick={() => closeDialog(false)} aria-label="close">
                  <CloseIcon />
                </IconButton>
                <Typography variant="h6">{t('f-supp-order-complete-title')}</Typography>
              </Box>

              <IconButton color="inherit" onClick={handleCompleteOrder} disabled={isBusy} id="saveBtn">
                <Icon>done</Icon>
              </IconButton>
            </Box>
          </Toolbar>
        </AppBar>
      )}

      <DialogTitle id="completeOrderDialogTitle">{t('f-supp-order-complete-title')}</DialogTitle>

      <DialogContent>
        <Grid container direction="column">
          {renderOrderItemsSectionHeader()}

          {renderOrderItemsTable()}

          {renderDetailsSectionHeader()}

          {renderDetailsSectionContent()}
        </Grid>
      </DialogContent>

      {!fullScreen && (
        <DialogActions>
          {isBusy && <CircularProgress />}

          <Button
            onClick={() => closeDialog(false)}
            startIcon={<Icon>{'close'}</Icon>}
            disabled={isBusy}
            variant="outlined"
            color="primary"
          >
            {t('f-com-cancel')}
          </Button>

          <Button
            onClick={handleCompleteOrder}
            id="saveBtn"
            startIcon={<Icon>{'done_outline'}</Icon>}
            disabled={isBusy}
            variant="contained"
            color="primary"
          >
            {t('f-supp-order-complete')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CompleteMedicalSupplyOrderModal;
